import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, ScanCommand, GetCommand } from '@aws-sdk/lib-dynamodb';
import { getConfigValue } from './configService';
import { awsCredentialsService } from './awsCredentialsService';
import { activityService } from './activityService';
import { portalAdmin } from './portalAdmin';
import type { Contract } from '@/config/contract';
import type { AccountOrgValue, ClientMetadata, DeployBlock, InactiveTag } from '@/types';

/**
 * The fields of a client's row that a form may change. A field left out is
 * not touched. `null` removes the field from the row.
 *
 * The two tags (do not deploy, inactive) are not here on purpose: they have
 * their own set and lift calls, so no form can change one by accident.
 */
export interface MetadataFields {
  contract?: Contract | null;
  partner?: string | null;
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  notes?: string | null;
  accountOrg?: AccountOrgValue | null;
}

/** What a tag call found: the tag as it now stands, and whether this call changed the row. */
export interface TagResult<T> {
  tag: T | undefined;
  /** False when the row already stood that way: someone else got there first. Nothing was written or logged. */
  changed: boolean;
}

/**
 * The portal's own record of each client: contract, partner, trial dates,
 * notes, organisation and the two tags. None of it is deployed.
 *
 * Every write changes named fields only. There is no call that saves a whole
 * row: a form that saved the row it loaded would delete any field it did not
 * know, and any tag a second person set in the meantime.
 *
 * The browser only reads the table. Each write is a portal-admin operation
 * (TASK-630), which keeps those rules on the server, takes who set a tag from
 * the sign-in, and writes the activity row.
 */
export class ClientMetadataService {
  private cache: Map<string, ClientMetadata> | null = null;
  private lastFetchTime = 0;
  private readonly cacheDuration = 5 * 60 * 1000; // 5 minutes
  private inflight: Promise<Map<string, ClientMetadata>> | null = null;
  /** Goes up at each write. A read that started before a write never fills the cache. */
  private generation = 0;
  private listeners = new Set<() => void>();

  /** The page's AWS credentials, or undefined before the portal config has loaded. */
  private getCredentialsProvider() {
    return awsCredentialsService.credentialsIfConfigured();
  }

  private getTableName(): string | null {
    return getConfigValue('CLIENT_METADATA_TABLE');
  }

  /** The table and a client for it, or null when the portal has no metadata table or nobody is signed in. */
  private reader(): { doc: DynamoDBDocumentClient; tableName: string } | null {
    const tableName = this.getTableName();
    const credentials = this.getCredentialsProvider();
    if (!tableName || !credentials) return null;
    const region = getConfigValue('AWS_REGION') || 'us-east-1';
    return {
      doc: DynamoDBDocumentClient.from(
        awsCredentialsService.withRevokedRetry(new DynamoDBClient({ region, credentials }))
      ),
      tableName,
    };
  }

  /**
   * Every client's row, by client name. It throws when the read fails, so a
   * caller that enforces something (a deploy block) can never mistake a failed
   * read for "no tags". Kept for five minutes; `fresh` reads the table again.
   */
  async loadAllMetadata(options: { fresh?: boolean } = {}): Promise<Map<string, ClientMetadata>> {
    const now = Date.now();
    if (!options.fresh) {
      if (this.cache && now - this.lastFetchTime < this.cacheDuration) return this.cache;
      // Many parts of a page ask at once. They share one read.
      if (this.inflight) return this.inflight;
    }

    const target = this.reader();
    if (!target) return new Map();

    const startedAt = this.generation;
    const read = (async () => {
      const result = new Map<string, ClientMetadata>();
      let exclusiveStartKey: Record<string, unknown> | undefined;
      do {
        const response = await target.doc.send(
          new ScanCommand({
            TableName: target.tableName,
            ExclusiveStartKey: exclusiveStartKey,
            // A read straight after a write must show the write.
            ConsistentRead: true,
          })
        );
        for (const item of response.Items || []) {
          const metadata = item as ClientMetadata;
          if (metadata.clientName) result.set(metadata.clientName, metadata);
        }
        exclusiveStartKey = response.LastEvaluatedKey as Record<string, unknown> | undefined;
      } while (exclusiveStartKey);

      if (this.generation === startedAt) {
        this.cache = result;
        this.lastFetchTime = Date.now();
        // A read somebody asked for by name can hold newer tags than the pages show. Tell them.
        if (options.fresh) this.notify();
      }
      return result;
    })();

    this.inflight = read;
    try {
      return await read;
    } finally {
      if (this.inflight === read) this.inflight = null;
    }
  }

  /**
   * Every client's row, or an empty map when the read fails. For a page that
   * only shows the rows. Anything that decides from them uses `loadAllMetadata`.
   */
  async getAllMetadata(): Promise<Map<string, ClientMetadata>> {
    try {
      return await this.loadAllMetadata();
    } catch (error) {
      console.error('Failed to fetch client metadata:', error);
      return new Map();
    }
  }

  async getMetadata(clientName: string): Promise<ClientMetadata | undefined> {
    const all = await this.getAllMetadata();
    return all.get(clientName);
  }

  /** One client's row as it stands in the table this instant. It throws when the read fails. */
  async getMetadataFresh(clientName: string): Promise<ClientMetadata | undefined> {
    const target = this.reader();
    if (!target) return undefined;
    const response = await target.doc.send(
      new GetCommand({ TableName: target.tableName, Key: { clientName }, ConsistentRead: true })
    );
    return response.Item as ClientMetadata | undefined;
  }

  /**
   * Change the named fields of one client's row and nothing else. A field
   * left out of `fields` keeps its value, as does every field this call does
   * not know (the two tags among them). A row that does not exist is made.
   * A change of contract or partner goes to the activity log.
   */
  async updateFields(clientName: string, fields: MetadataFields): Promise<void> {
    const named = Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined));
    if (Object.keys(named).length === 0) return;
    try {
      await portalAdmin('clientMetadata.updateFields', { clientName, fields: named });
    } finally {
      this.changed();
    }
  }

  /**
   * Mark a client "do not deploy". Only the tag and the two "last changed"
   * fields are written. If the client already carries the tag, nothing is
   * written and the standing tag comes back with `changed: false`.
   */
  async setDeployBlock(clientName: string, reason: string): Promise<TagResult<DeployBlock>> {
    const text = reason.trim();
    if (!text) throw new Error('Give the reason deploys are blocked.');
    return this.setTag<DeployBlock>(clientName, 'deployBlock', text);
  }

  /** Allow deploys to a client again: the tag is removed from the row. */
  async liftDeployBlock(clientName: string): Promise<TagResult<DeployBlock>> {
    return this.liftTag<DeployBlock>(clientName, 'deployBlock');
  }

  /** Mark a client inactive. The reason is optional. */
  async setInactive(clientName: string, reason?: string): Promise<TagResult<InactiveTag>> {
    return this.setTag<InactiveTag>(clientName, 'inactive', reason?.trim() || undefined);
  }

  /** Mark a client active again: the tag is removed from the row. */
  async liftInactive(clientName: string): Promise<TagResult<InactiveTag>> {
    return this.liftTag<InactiveTag>(clientName, 'inactive');
  }

  /**
   * Record that a person deployed to a client in spite of its block. The
   * block itself stays on the row.
   */
  async logDeployBlockOverride(
    clientName: string,
    block: DeployBlock,
    run: { deploymentId: string; imageTag?: string }
  ): Promise<void> {
    await this.log(clientName, 'deploy-block-overridden', {
      reason: block.reason,
      setBy: block.setBy,
      setAt: block.setAt,
      deploymentId: run.deploymentId,
      ...(run.imageTag ? { imageTag: run.imageTag } : {}),
    });
  }

  /** Forget the rows held in memory. The next read goes to the table. */
  clearCache(): void {
    this.changed();
  }

  /** Be told when the rows change or the cache is cleared. Returns the call that stops it. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private changed(): void {
    this.generation += 1;
    this.cache = null;
    this.lastFetchTime = 0;
    this.notify();
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener();
  }

  /** Put a tag on a row that does not carry it. The server says whether this call changed the row. */
  private async setTag<T>(
    clientName: string,
    tag: 'deployBlock' | 'inactive',
    reason: string | undefined
  ): Promise<TagResult<T>> {
    try {
      return await portalAdmin<TagResult<T>>('clientMetadata.setTag', {
        clientName,
        tag,
        ...(reason ? { reason } : {}),
      });
    } finally {
      this.changed();
    }
  }

  /** Take a tag off a row. `changed` is false when there was none. */
  private async liftTag<T>(clientName: string, tag: 'deployBlock' | 'inactive'): Promise<TagResult<T>> {
    try {
      const result = await portalAdmin<{ changed: boolean }>('clientMetadata.liftTag', { clientName, tag });
      return { tag: undefined, changed: result.changed };
    } finally {
      this.changed();
    }
  }

  private log(clientName: string, action: string, metadata: Record<string, unknown>): Promise<void> {
    return activityService.logActivity({
      type: 'config',
      action,
      resourceType: 'client-flags',
      resourceId: clientName,
      details: { metadata },
      success: true,
    });
  }
}

export const clientMetadataService = new ClientMetadataService();
